import { Button } from "../../components/ui/button";
import { toast } from "sonner";
import { Sparkles, CheckCircle2, XCircle, Share2 } from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";
import { waShareUrl } from "../../lib/api";
import { inr } from "./mockData";

export function TreasurerDashboard({ flats, queue, onConfirm, onReject }) {
  const paid = flats.filter((f) => f.status === "paid");
  const pendingFlats = flats.filter((f) => f.status !== "paid");
  const totalExpected = 78600;
  const collected = totalExpected - pendingFlats.reduce((s, f) => s + f.dueNow + (f.arrears || 0), 0);
  const pct = Math.round((collected / totalExpected) * 100);

  const summaryText = `📊 Sunrise Residency — Maintenance update (June 2026)\n✅ Collected: ${inr(collected)} of ${inr(totalExpected)} (${pct}%)\n⏳ ${pendingFlats.length} of ${flats.length} flats pending\nGenerated by NivasOne — no more Excel! 🙌`;

  return (
    <div className="space-y-5">
      <div className="rounded-3xl bg-stone-900 text-white p-5" data-testid="collection-summary-card">
        <div className="flex items-start justify-between">
          <div>
            <div className="text-xs text-stone-400 uppercase tracking-wider">June 2026 · Collection</div>
            <div className="font-serif-display text-3xl mt-1">{inr(collected)}</div>
            <div className="text-sm text-stone-400">of {inr(totalExpected)} expected</div>
          </div>
          <div className="text-right">
            <div className="text-2xl font-bold text-emerald-400">{pct}%</div>
            <div className="text-xs text-stone-400">{paid.length}/{flats.length} flats settled</div>
          </div>
        </div>
        <div className="mt-4 h-2.5 rounded-full bg-stone-700 overflow-hidden">
          <div className="h-full rounded-full bg-emerald-400 transition-all duration-700" style={{ width: `${pct}%` }} />
        </div>
        <div className="mt-4 flex items-center justify-between">
          <div className="text-xs text-stone-400">{pendingFlats.length} flats pending · {inr(totalExpected - collected)} outstanding</div>
          <button onClick={() => window.open(waShareUrl(summaryText), "_blank")} data-testid="share-summary-btn"
            className="inline-flex items-center gap-1.5 text-xs bg-[#25D366] text-white px-3 py-1.5 rounded-full hover:bg-[#1EBE55] transition">
            <FaWhatsapp size={13} /> Post summary to group
          </button>
        </div>
      </div>

      <div>
        <div className="flex items-center gap-2 mb-2">
          <h2 className="text-base font-semibold">To confirm</h2>
          {queue.length > 0 && <span className="text-xs bg-sky-100 text-sky-700 px-2 py-0.5 rounded-full font-semibold">{queue.length}</span>}
        </div>
        {queue.length === 0 && (
          <div className="rounded-2xl border border-dashed border-stone-300 p-6 text-center text-sm text-stone-400" data-testid="empty-confirm-queue">
            All caught up — nothing waiting for confirmation 🎉
          </div>
        )}
        <div className="space-y-3">
          {queue.map((q) => (
            <div key={q.id} className="rounded-2xl border border-sky-200 bg-sky-50/50 p-4" data-testid={`confirm-card-${q.flat}`}>
              <div className="flex items-center justify-between">
                <div className="font-semibold text-sm">{q.flat} · {q.name}</div>
                <span className="text-[10px] bg-sky-100 text-sky-700 px-2 py-0.5 rounded-full">{q.source}</span>
              </div>
              {q.smsText && (
                <div className="mt-2 text-[11px] font-mono bg-white border border-stone-200 rounded-xl p-2.5 text-stone-600">{q.smsText}</div>
              )}
              <div className="mt-2 rounded-xl bg-white border border-emerald-200 p-3">
                <div className="flex items-center gap-1 text-[11px] font-semibold text-emerald-700 mb-1.5"><Sparkles size={12} /> AI extracted</div>
                <div className="grid grid-cols-2 gap-y-1 text-xs">
                  <span className="text-stone-500">Amount</span><b>{inr(q.parsed.amount)}</b>
                  <span className="text-stone-500">Date</span><b>{q.parsed.date}</b>
                  <span className="text-stone-500">UTR / Ref</span><b>{q.parsed.utr}</b>
                  <span className="text-stone-500">Payer</span><b>{q.parsed.payer}</b>
                </div>
                <div className="mt-1.5 text-[11px] text-stone-500">Allocates to: <b className="text-stone-800">{q.allocation}</b></div>
              </div>
              <div className="mt-3 flex gap-2">
                <Button size="sm" onClick={() => { onConfirm(q); toast.success(`Confirmed · Receipt generated for ${q.flat}`); }}
                  data-testid={`confirm-btn-${q.flat}`} className="flex-1 bg-emerald-600 hover:bg-emerald-700 rounded-full">
                  <CheckCircle2 size={15} className="mr-1.5" /> Confirm & receipt
                </Button>
                <Button size="sm" variant="outline" onClick={() => { onReject(q); toast("Sent back with a note to the resident"); }}
                  data-testid={`reject-btn-${q.flat}`} className="rounded-full border-stone-300 text-stone-600">
                  <XCircle size={15} className="mr-1.5" /> Not matching
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="rounded-2xl border border-stone-200 bg-white p-4">
        <div className="flex items-center gap-2 text-sm font-semibold"><Share2 size={15} className="text-[#C05640]" /> Why treasurers like this</div>
        <ul className="mt-2 space-y-1.5 text-xs text-stone-600 list-disc pl-4">
          <li>Screenshots from WhatsApp become confirmed entries in 2 taps — no Excel</li>
          <li>The app sends reminders, not you — no awkward conversations</li>
          <li>Every rupee has a numbered receipt & audit trail — protects the treasurer</li>
        </ul>
      </div>
    </div>
  );
}
